import Head from 'next/head';
import PropTypes from 'prop-types';
import { useContext } from 'react';
import AppContext from '../../../context/AppContext';
import { addTrailingSlash, ucFirst } from '../../../utils/utility';

export const parseForSeo = (posts) => {
	const result = posts.map((post, index) => {
		const {
			author,
			featuredImage,
			permalink,
			postId,
			title,
		} = post;

		const seo = {
			author,
			id: postId,
			image: featuredImage,
			name: title,
			position: index + 1,
			url: permalink,
		};

		return seo;
	});

	return result;
};

export default function SeoJsonLd({ description, pagename, seo }) {
	const appContext = useContext(AppContext);
	const { asPath, hostUrl } = appContext;

	const href = addTrailingSlash(`${hostUrl}${asPath}`);

	const location = asPath.split('/');

	const itemListElement = seo.map((item) => `{
		"@type": "ListItem",
		"@id": "${item?.id}",
		"author": "${item?.author}",
		"image": "${item?.image}",
		"name": "${item?.name}",
		"position": ${item?.position},
		"url": "${item?.url}"
	}`);

	const website = `
		"@type": "WebSite",
		"@id": "${hostUrl}#website",
		"url": "${hostUrl}",
		"name": "Flavour Network",
		"description": "${description}",
		"publisher": {
			"@id": "${hostUrl}#organization"
		},
		"inLanguage": "en-CA"
	`;

	const collectionPage = `
		"@type": "CollectionPage",
		"@id": "${href}#webpage",
		"url": "${href}",
		"name": "${pagename}",
		"isPartOf": {
			"@id": "${hostUrl}#website"
		},
		"description": "${description}",
		"breadcrumb": {
			"@id": "${href}#breadcrumb"
		},
		"inLanguage": "en-CA",
		"potentialAction": [
			{
				"@type": "ReadAction",
				"target": [
					"${href}"
				]
			}
		]
	`;

	const breadcrumbList = `
		"@type": "BreadcrumbList",
		"@id": "${href}#breadcrumb",
		"itemListElement": [
			{
				"@type": "ListItem",
				"position": "1",
				"name": "Home",
				"item": "${hostUrl}"
			},
			{
				"@type": "ListItem",
				"position": "2",
				"name": "${ucFirst(location[1]) || ucFirst(location[0])}",
				"item": "${href}"
			}
		]
	`;

	return (
		<Head>
			<script
				type="application/ld+json"
				// eslint-disable-next-line react/no-danger
				dangerouslySetInnerHTML={{
					__html: `
					{
						"@context": "https://schema.org",
						"name": "${pagename}",
						"description": "${description}",
						"@graph": [
							{
								${website}
							},
							{
								${collectionPage}
							},
							{
								${breadcrumbList}
							},
							{
								"@type": "ItemList",
								"itemListElement": [${itemListElement}]
							}
						]
					}`,
				}}
			/>
		</Head>
	);
}

SeoJsonLd.propTypes = {
	description: PropTypes.string.isRequired,
	pagename: PropTypes.string.isRequired,
	seo: PropTypes.array.isRequired,
};
